/**
 * Utility functions for ensuring proper tour element positioning
 * Fixes misaligned highlights by checking element readiness
 */

export interface ElementReadinessOptions {
  selector?: string;
  timeout?: number;
  checkInterval?: number;
  minDimensions?: { width: number; height: number };
}

/**
 * Waits for an element to be fully rendered and positioned
 */
export const waitForElementReadiness = (
  selector: string,
  options: ElementReadinessOptions = {}
): Promise<Element | null> => {
  const {
    timeout = 5000,
    checkInterval = 100,
    minDimensions = { width: 10, height: 10 }
  } = options;

  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkElement = () => {
      const element = document.querySelector(selector);
      
      if (!element) {
        // Element doesn't exist yet
        if (Date.now() - startTime < timeout) {
          setTimeout(checkElement, checkInterval);
        } else {
          resolve(null);
        }
        return;
      }

      // Check if element is properly positioned and visible
      const rect = element.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(element);
      
      const isVisible = computedStyle.visibility !== 'hidden' && 
                       computedStyle.display !== 'none' &&
                       computedStyle.opacity !== '0';
      
      const hasValidDimensions = rect.width >= minDimensions.width && 
                                rect.height >= minDimensions.height;
      
      const isInViewport = rect.top >= 0 && rect.left >= 0;

      if (isVisible && hasValidDimensions && isInViewport) {
        // Element is ready
        resolve(element);
      } else if (Date.now() - startTime < timeout) {
        // Keep checking
        setTimeout(checkElement, checkInterval);
      } else {
        // Timeout reached, return element anyway
        resolve(element);
      }
    };

    checkElement();
  });
};

/**
 * Waits for multiple elements to be ready
 */
export const waitForMultipleElements = async (
  selectors: string[],
  options: ElementReadinessOptions = {}
): Promise<(Element | null)[]> => {
  const promises = selectors.map(selector => 
    waitForElementReadiness(selector, options)
  );
  
  return Promise.all(promises);
};

/**
 * Waits for page to be fully loaded and stable
 */
export const waitForPageStability = (
  additionalDelay: number = 500
): Promise<void> => {
  return new Promise((resolve) => {
    // Wait for document ready state
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(resolve, additionalDelay);
      });
    } else if (document.readyState === 'interactive') {
      // DOM is loaded but resources might still be loading
      window.addEventListener('load', () => {
        setTimeout(resolve, additionalDelay);
      });
    } else {
      // Document is fully loaded
      setTimeout(resolve, additionalDelay);
    }
  });
};

/**
 * Forces a tour library to recalculate positions
 * This is a generic approach that works with most tour libraries
 */
export const triggerTourRecalculation = () => {
  // Dispatch a resize event to force tour libraries to recalculate
  window.dispatchEvent(new Event('resize'));
  
  // Also trigger scroll event as some libraries listen to it
  window.dispatchEvent(new Event('scroll'));
  
  // Force a small layout shift to trigger reposition
  const body = document.body;
  if (body) {
    const originalPadding = body.style.paddingTop;
    body.style.paddingTop = '0.1px';
    
    // Restore after a tick
    setTimeout(() => {
      body.style.paddingTop = originalPadding;
    }, 10);
  }
};

/**
 * Enhanced tour start function with proper positioning
 */
export const startTourWithProperPositioning = async (
  tourStartFunction: () => void,
  targetSelectors: string[] = [],
  options: {
    pageStabilityDelay?: number;
    elementReadinessTimeout?: number;
    finalDelay?: number;
  } = {}
): Promise<void> => {
  const {
    pageStabilityDelay = 500,
    elementReadinessTimeout = 3000,
    finalDelay = 300
  } = options;

  try {
    // Step 1: Wait for page to be stable
    await waitForPageStability(pageStabilityDelay);
    
    // Step 2: Wait for target elements to be ready (if provided)
    if (targetSelectors.length > 0) {
      await waitForMultipleElements(targetSelectors, {
        timeout: elementReadinessTimeout
      });
    }
    
    // Step 3: Start the tour
    tourStartFunction();
    
    // Step 4: Give the tour a moment to initialize, then trigger recalculation
    setTimeout(() => {
      triggerTourRecalculation();
    }, finalDelay);
    
  } catch (error) {
    console.warn('Tour positioning setup encountered an issue:', error);
    // Start tour anyway after a delay
    setTimeout(tourStartFunction, 1000);
  }
};

/**
 * Debounced recalculation for dynamic content
 */
let recalculationTimeout: NodeJS.Timeout;

export const debouncedTourRecalculation = (delay: number = 300) => {
  if (recalculationTimeout) {
    clearTimeout(recalculationTimeout);
  }
  
  recalculationTimeout = setTimeout(() => {
    triggerTourRecalculation();
  }, delay);
};
